// lib/services/console/console_service.ts

// A tiny, framework-agnostic in-app debug console. It exists so that on phones
// (where remote browser devtools are painful to attach) engine code can surface
// diagnostics into a scrollable on-screen panel.
//
// Design constraints:
//   - No React and no browser devtools dependency. It never monkey-patches the
//     global `console`; callers opt in explicitly via console_service.log(...).
//   - Construction is cheap and SSR-safe (no browser APIs touched), so Stage can
//     own one at construction time like its other services.
//   - Bounded history so a long-running session can't grow memory without limit.
//   - A snapshot/subscribe pair shaped for React's useSyncExternalStore: the
//     snapshot object identity is stable between mutations, so consumers only
//     re-render when something actually changed.

export interface ConsoleLine {
  // Monotonic id, useful as a stable React key and to preserve arrival order.
  id: number;
  // Wall-clock time the line was logged (ms since epoch).
  timestamp: number;
  // The original message text, stored verbatim.
  text: string;
}

export interface ConsoleSnapshot {
  lines: readonly ConsoleLine[];
  visible: boolean;
}

export type ConsoleListener = () => void;

// Bounded so a long session can't grow unbounded. Oldest lines are dropped
// first; arrival order of the survivors is preserved.
const DEFAULT_MAX_LINES = 800;

export default class ConsoleService {
  private readonly max_lines: number;
  private lines: ConsoleLine[] = [];
  private visible = false;
  private next_id = 1;

  private readonly listeners = new Set<ConsoleListener>();
  // Cached immutable snapshot; rebuilt only when state changes so
  // useSyncExternalStore sees a stable reference between mutations.
  private snapshot: ConsoleSnapshot;

  constructor(max_lines: number = DEFAULT_MAX_LINES) {
    this.max_lines = Math.max(1, Math.floor(max_lines));
    this.snapshot = { lines: [], visible: false };
  }

  // ---- Logging -----------------------------------------------------------

  // Append a line. Verbatim text is preserved; a timestamp/id are attached as
  // metadata. Multi-line strings are kept as a single logical line so the UI
  // can render their line breaks without reordering.
  log(message: string): void {
    const text = typeof message === 'string' ? message : String(message);
    this.mirror_to_native_console(text);
    this.lines.push({ id: this.next_id++, timestamp: Date.now(), text });
    if (this.lines.length > this.max_lines) {
      this.lines.splice(0, this.lines.length - this.max_lines);
    }
    this.commit();
  }

  clear(): void {
    if (this.lines.length === 0) return;
    this.lines = [];
    this.commit();
  }

  // ---- Visibility --------------------------------------------------------

  show(): void {
    this.set_visible(true);
  }

  hide(): void {
    this.set_visible(false);
  }

  toggle(): void {
    this.set_visible(!this.visible);
  }

  is_visible(): boolean {
    return this.visible;
  }

  private set_visible(next: boolean): void {
    if (this.visible === next) return;
    this.visible = next;
    this.commit();
  }

  // ---- React-friendly store interface ------------------------------------

  // Stable getSnapshot for useSyncExternalStore. Returns the same object
  // reference until the next mutation.
  getSnapshot = (): ConsoleSnapshot => this.snapshot;

  subscribe = (listener: ConsoleListener): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  private mirror_to_native_console(text: string): void {
    // Best-effort mirror for easier copy/paste from DevTools. Kept here at the
    // central service boundary so callers still make one service log call.
    // Wrapped defensively so SSR/tests/custom consoles can never break the
    // in-app console path.
    try {
      const logger =
        typeof console !== 'undefined' && typeof console.log === 'function'
          ? console.log
          : null;
      if (!logger) return;
      logger.call(console, text);
    } catch {
      // Native console mirroring is diagnostic-only.
    }
  }

  private commit(): void {
    this.snapshot = { lines: this.lines.slice(), visible: this.visible };
    for (const listener of this.listeners) listener();
  }
}
